import { Injectable } from '@angular/core';
import { CustomCart } from '@cart/const';
import { ActiveCartService, CartActions } from '@cart/data-access';
import { PaymentMode } from '@checkout/const';
import { CheckoutActions, CheckoutApiService } from '@checkout/data-access';
import { isCheckoutRoute } from '@checkout/utils';
import { CityActions } from '@city/data-access';
import { SnackTheme } from '@main/snackbar/const';
import { SnackbarFacade } from '@main/snackbar/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { BankCard, PaymentModeCodes } from '@payment/const';
import { PaymentActions } from '@payment/data-access';
import { PaymentService } from '@payment/feature';
import { ActivatedRouterStateSnapshot } from '@spartacus/core';
import { CustomUserActions } from '@user/data-access';
import { CurrentProfileService } from '@user/feature';
import { Observable, combineLatest, from, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { PaymentModeService } from '../services';

@Injectable()
export class PaymentEffects {
  loadCartOrLoadReceiptTypeOrChangeCity$: Observable<
    CheckoutActions.LoadPaymentModeListReset | CheckoutActions.LoadPaymentModeList
  > = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(CartActions.LOAD_CART_FAIL, CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS)),
      this.actions$.pipe(
        ofType(CartActions.LOAD_CART_SUCCESS),
        map<CartActions.LoadCartSuccess, CustomCart>(({ payload }) => payload.cart),
        map((cart) => cart.isInflow || false),
        distinctUntilChanged(),
      ),
      this.actions$.pipe(
        ofType(CityActions.SET_ACTIVE_BASE_STORE),
        map(({ payload }) => payload),
        distinctUntilChanged(),
        skip(1),
      ),
    ).pipe(
      switchMap(() =>
        from([new CheckoutActions.LoadPaymentModeListReset(), new CheckoutActions.LoadPaymentModeList()]),
      ),
    ),
  );

  loadList$: Observable<CheckoutActions.LoadPaymentModeListSuccess | CheckoutActions.LoadPaymentModeListFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CheckoutActions.LOAD_PAYMENT_MODE_LIST),
        mergeMap(() =>
          combineLatest({
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(
            take(1),
            filter(({ cartId }) => !!cartId),
          ),
        ),
        switchMap(({ cartId }: { cartId: string }) =>
          this.checkoutApiService.loadPaymentModes(cartId).pipe(
            map((paymentModes) => new CheckoutActions.LoadPaymentModeListSuccess(paymentModes)),
            catchError((error) => of(new CheckoutActions.LoadPaymentModeListFail(error))),
          ),
        ),
      ),
    );

  // если нам пришлось перезапросить список пейментмодов, значит. скорее всего там скинулась выбранная оплата
  // и надо её проставить заново
  // если в новом списке будет содержаться выбранный до этого пейментмод
  setCurrentPaymentOnLoadPaymentListSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.LOAD_PAYMENT_MODE_LIST_SUCCESS),
        concatLatestFrom(() => [
          this.paymentModeService.getActivePaymentCode(),
          this.paymentService.getActivePayment(),
        ]),
        map(
          ([action, activePaymentModeCode, activePayment]: [
            CheckoutActions.LoadPaymentModeListSuccess,
            string | undefined,
            BankCard | null,
          ]) => {
            const { payload: modes } = action;

            if (!modes.find((mode: PaymentMode) => mode.code === activePaymentModeCode)) {
              this.paymentModeService.resetActivePaymentMode();

              return;
            }

            if (
              modes.find((mode: PaymentMode) => mode.code === activePaymentModeCode) ||
              (activePaymentModeCode === PaymentModeCodes.Sbrf && activePayment.bindingId)
            ) {
              this.paymentModeService.setActivePaymentMode(activePaymentModeCode);
            }

            if (activePaymentModeCode === PaymentModeCodes.Sbrf) {
              if (activePayment) {
                this.paymentService.setActivePayment(activePayment);
              } else {
                this.paymentModeService.resetActivePaymentMode();
              }
            }

            return;
          },
        ),
      ),
    { dispatch: false },
  );

  setPayment$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.SET_PAYMENT),
        map((action: CheckoutActions.SetPayment) => {
          return action.payload;
        }),
        mergeMap((payload) =>
          combineLatest({
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(
            take(1),
            map(({ cartId }) => ({ cartId, paymentType: payload })),
          ),
        ),
        filter(({ cartId }) => !!cartId),
        switchMap(({ cartId, paymentType }: { cartId: string; paymentType: string }) =>
          this.checkoutApiService
            .setPayment(cartId, paymentType)
            .pipe(catchError(() => of(new CheckoutActions.SetPaymentFail()))),
        ),
      ),
    { dispatch: false },
  );

  deletePaymentOnInit$: Observable<CheckoutActions.DeletePayment> = createEffect(() =>
    this.actions$.pipe(
      take(1),
      concatLatestFrom(() => this.currentProfileService.get()),
      filter(([, profile]) => profile.isLegal),
      map(() => new CheckoutActions.DeletePayment()),
    ),
  );

  deletePaymentOnChangeLegalStatus$: Observable<CheckoutActions.DeletePayment> = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomUserActions.SetLegalEntityStatus>(CustomUserActions.SET_LEGAL_ENTITY_STATUS),
      filter(({ payload }) => Boolean(payload)),
      map(() => {
        return new CheckoutActions.DeletePayment();
      }),
    ),
  );

  deletePayment$: Observable<CheckoutActions.DeletePaymentFail | CheckoutActions.ResetPayment> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.DeletePayment>(CheckoutActions.DELETE_PAYMENT),
      mergeMap(() =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(take(1)),
      ),
      filter(({ cartId }) => !!cartId),
      switchMap(({ cartId }: { cartId: string }) =>
        this.checkoutApiService.deletePayment(cartId).pipe(
          map(() => new CheckoutActions.ResetPayment()),
          catchError(() => of(new CheckoutActions.DeletePaymentFail())),
        ),
      ),
    ),
  );

  resetPaymentOnEnterCheckout$: Observable<CheckoutActions.ResetPayment> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map(({ payload: { routerState } }) => routerState),
      filter((routerState: ActivatedRouterStateSnapshot) => isCheckoutRoute(routerState.semanticRoute)),
      map(() => {
        return new CheckoutActions.ResetPayment();
      }),
    ),
  );

  bindPaymentFail$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PaymentActions.BindPaymentFail>(PaymentActions.BIND_PAYMENT_FAIL),
        map(() => {
          this.paymentModeService.resetActivePaymentMode();

          this.snackbarFacade.setSnack({
            title: 'Ошибка',
            text: 'Не удалось привязать вашу банковскую карту',
            theme: SnackTheme.Yellow,
          });

          return;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private checkoutApiService: CheckoutApiService,
    private activeCartService: ActiveCartService,
    private currentProfileService: CurrentProfileService,
    private paymentService: PaymentService,
    private paymentModeService: PaymentModeService,
    private snackbarFacade: SnackbarFacade,
  ) {}
}
